import React from 'react';

interface MaintenanceScreenProps {
    message: string;
    onLogout: () => void;
}

export const MaintenanceScreen: React.FC<MaintenanceScreenProps> = ({ message, onLogout }) => {
    return (
        <div className="maintenance-screen">
            {/* Animated background particles */}
            <div className="particles">
                {[...Array(20)].map((_, i) => (
                    <div key={i} className="particle" style={{
                        left: `${Math.random() * 100}%`,
                        animationDelay: `${Math.random() * 5}s`,
                        animationDuration: `${15 + Math.random() * 10}s`
                    }} />
                ))}
            </div>

            {/* Gradient orbs */}
            <div className="orb orb-1" />
            <div className="orb orb-2" />
            <div className="orb orb-3" />

            {/* Content */}
            <div className="content">
                {/* Animated logo/icon */}
                <div className="icon-container">
                    <div className="icon-glow" />
                    <div className="icon-ring" />
                    <div className="icon-ring icon-ring-2" />
                    <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>

                {/* Title */}
                <h1 className="title">
                    <span className="title-line">We're Building</span>
                    <span className="title-highlight">Something Amazing</span>
                </h1>

                {/* Message */}
                <p className="message">
                    {message || "Our studio is getting a fresh coat of paint. We'll be back with something incredible."}
                </p>

                {/* Progress indicator */}
                <div className="progress-container">
                    <div className="progress-bar">
                        <div className="progress-fill" />
                        <div className="progress-glow" />
                    </div>
                    <span className="progress-text">Launching Soon</span>
                </div>

                {/* Sign out button */}
                <button className="sign-out-btn" onClick={onLogout}>
                    <span>Sign Out</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16,17 21,12 16,7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                </button>
            </div>

            <style>{`
        .maintenance-screen {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #06060a;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          overflow: hidden;
          z-index: 9999;
        }

        /* Floating particles */
        .particles {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
        }

        .particle {
          position: absolute;
          bottom: -20px;
          width: 4px;
          height: 4px;
          background: rgba(139, 92, 246, 0.6);
          border-radius: 50%;
          animation: float-up linear infinite;
          box-shadow: 0 0 10px rgba(139, 92, 246, 0.8);
        }

        @keyframes float-up {
          0% {
            transform: translateY(0) scale(1);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100vh) scale(0);
            opacity: 0;
          }
        }

        /* Gradient orbs */
        .orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.5;
          animation: orb-float 8s ease-in-out infinite;
        }

        .orb-1 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, transparent 70%);
          top: -200px;
          right: -100px;
          animation-delay: 0s;
        }

        .orb-2 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%);
          bottom: -150px;
          left: -100px;
          animation-delay: -3s;
        }

        .orb-3 {
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(236, 72, 153, 0.25) 0%, transparent 70%);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: -5s;
        }

        @keyframes orb-float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -30px) scale(1.05);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.95);
          }
        }

        /* Content container */
        .content {
          position: relative;
          z-index: 10;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 2rem;
          max-width: 500px;
        }

        /* Icon */
        .icon-container {
          position: relative;
          width: 100px;
          height: 100px;
          margin-bottom: 2.5rem;
        }

        .icon-glow {
          position: absolute;
          inset: -20px;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, transparent 60%);
          animation: pulse-glow 3s ease-in-out infinite;
        }

        .icon-ring {
          position: absolute;
          inset: -10px;
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 50%;
          animation: ring-rotate 10s linear infinite;
        }

        .icon-ring-2 {
          inset: -20px;
          animation-direction: reverse;
          animation-duration: 15s;
          border-color: rgba(59, 130, 246, 0.2);
        }

        .icon {
          position: absolute;
          inset: 15px;
          color: #a78bfa;
          animation: icon-float 4s ease-in-out infinite;
        }

        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }

        @keyframes ring-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes icon-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        /* Title */
        .title {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          margin-bottom: 1.25rem;
        }

        .title-line {
          font-size: 1rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.3em;
          color: rgba(255, 255, 255, 0.5);
        }

        .title-highlight {
          font-size: 2.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #fff 0%, #a78bfa 50%, #818cf8 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
        }

        /* Message */
        .message {
          font-size: 1.1rem;
          line-height: 1.7;
          color: rgba(255, 255, 255, 0.6);
          margin-bottom: 2.5rem;
          max-width: 380px;
        }

        /* Progress */
        .progress-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 2.5rem;
          width: 100%;
          max-width: 280px;
        }

        .progress-bar {
          position: relative;
          width: 100%;
          height: 4px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          width: 60%;
          background: linear-gradient(90deg, #8b5cf6 0%, #3b82f6 50%, #8b5cf6 100%);
          background-size: 200% 100%;
          border-radius: 4px;
          animation: shimmer 2s linear infinite;
        }

        .progress-glow {
          position: absolute;
          left: 55%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 20px;
          height: 20px;
          background: rgba(139, 92, 246, 0.8);
          border-radius: 50%;
          filter: blur(8px);
          animation: glow-pulse 2s ease-in-out infinite;
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        @keyframes glow-pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }

        .progress-text {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          color: rgba(139, 92, 246, 0.8);
        }

        /* Button */
        .sign-out-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.875rem 1.75rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.7);
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
        }

        .sign-out-btn:hover {
          color: #fff;
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(139, 92, 246, 0.5);
          box-shadow: 0 0 30px rgba(139, 92, 246, 0.2);
          transform: translateY(-2px);
        }

        .sign-out-btn svg {
          transition: transform 0.3s ease;
        }

        .sign-out-btn:hover svg {
          transform: translateX(3px);
        }
      `}</style>
        </div>
    );
};
